import { gql } from '@apollo/client'
import { Modal, VisuallyHidden } from '@farol-ds/react'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { handleTriggerExecution } from '@jusbrasil-web/shared-utils-url'
import { useCallback, useEffect, useState } from 'react'

import { CpfValidation_ViewerFragment } from './__generated__/cpf-validation.graphql'
import { ModalContextProvider } from './context'
import styles from './cpf-validation.module.scss'
import { Steps } from './enums'
import { StepRenderer } from './step-renderer'
import { CpfValidationEventData, CpfValidationTexts } from './types'

const CONTINUE_REPLACING_FLOW = 'continue-validate-flow'

function getReplacementTokenFromUrl(url?: string): string | null {
  if (!url) return null

  try {
    return new URL(url).searchParams.get(CONTINUE_REPLACING_FLOW)
  } catch {
    return null
  }
}

interface CpfValidationProps {
  viewer: CpfValidation_ViewerFragment | null
  isOpen: boolean
  eventData: CpfValidationEventData
  currentUrl?: string
  validateCallback?: (isValidatedSuccess: boolean) => void
  texts?: CpfValidationTexts
  onOpenChange?: (isOpen: boolean) => void
  validationOrigin: string
  onAfterValidationSuccess?: () => void | Promise<void>
}

export function CpfValidation(props: CpfValidationProps) {
  const {
    texts,
    currentUrl,
    isOpen,
    eventData,
    viewer,
    validateCallback,
    onOpenChange,
    validationOrigin,
    onAfterValidationSuccess,
  } = props

  const tokenFromUrl = getReplacementTokenFromUrl(currentUrl)
  const [replacementToken, setReplacementToken] = useState<string | null>(() => tokenFromUrl)
  const [currentStep, setCurrentStep] = useState<Steps>(() =>
    tokenFromUrl ? Steps.REPLACING_CPF : Steps.VALIDATE_IDENTITY
  )
  const [isReplacingFlow, setIsReplacingFlow] = useState(() => !!tokenFromUrl)

  useEffect(() => {
    if (tokenFromUrl) {
      setReplacementToken((currentToken) => currentToken ?? tokenFromUrl)
    }
  }, [tokenFromUrl])

  useEffect(() => {
    if (!replacementToken) return

    setIsReplacingFlow(true)
    setCurrentStep(Steps.REPLACING_CPF)
    handleTriggerExecution({ triggerName: CONTINUE_REPLACING_FLOW })
  }, [replacementToken])

  const onClose = useCallback(() => {
    onOpenChange?.(false)
    if (currentStep === Steps.REPLACEMENT_SUCCESS) {
      setIsReplacingFlow(false)
      setReplacementToken(null)
      setCurrentStep(Steps.VALIDATE_IDENTITY)
    }
  }, [onOpenChange, currentStep])

  // Separate handler from onClose, which also runs on success completion.
  const onIconClose = useCallback(() => {
    sendMetrics('UserAccount.IdValidationClosed', viewer?.pid?.toString() ?? null, {
      ...eventData,
      metadata: { ...eventData?.metadata, validation_origin: validationOrigin, step: currentStep },
    })
    onClose()
  }, [viewer, eventData, validationOrigin, currentStep, onClose])

  useEffect(() => {
    if (isOpen && !replacementToken) {
      setCurrentStep(Steps.VALIDATE_IDENTITY)
    }
  }, [isOpen, replacementToken])

  const isModalOpen = isOpen || isReplacingFlow
  if (!isModalOpen || !viewer) return null

  return (
    <Modal open={isModalOpen} onOpenChange={onOpenChange}>
      <VisuallyHidden>
        <Modal.Header>
          <Modal.HeaderTitle>Identity Validation</Modal.HeaderTitle>
        </Modal.Header>
      </VisuallyHidden>
      <Modal.Content className={styles.modalContent}>
        <ModalContextProvider
          setCurrentStep={setCurrentStep}
          replacementToken={replacementToken}
          eventData={eventData}
          viewer={viewer}
          validationOrigin={validationOrigin}
          texts={texts}
          onAfterValidationSuccess={onAfterValidationSuccess}
        >
          <Modal.IconClose onClick={onIconClose} data-testid="close-button" />
          <StepRenderer
            onClose={onClose}
            currentStep={currentStep}
            stepsCallback={validateCallback}
          />
        </ModalContextProvider>
      </Modal.Content>
    </Modal>
  )
}

CpfValidation.fragments = {
  viewer: gql`
    fragment CpfValidation_viewer on Profile {
      pid
      cpf
      birthDate
    }
  `,
}
